import { TreeNode } from 'primeng/api';
import { DashboardEconomyTimelineEntry } from '@interfaces/dashboard/responses/dashboard-economy-changes.response';
import { isAutomaticEconomyTrigger } from '@src/app/features/dashboard/dashboard-management/pages/economy-history/economy-history.labels';
import { isZeroAmount } from '@src/app/features/dashboard/dashboard-management/pages/economy-history/economy-history.summary';

/** Nível de cada linha da árvore da linha do tempo (card 3323). */
export type TimelineRowKind = 'day' | 'company' | 'reprocessGroup' | 'entry';

export interface TimelineRow {
  kind: TimelineRowKind;
  label: string;
  /** Versões gravadas debaixo deste nó (no evento, 1). */
  versionCount: number;
  /** Versões com evento (motivo que não é rodada automática). */
  eventCount: number;
  /** Economia ao fim do dia (dia só quando é uma empresa; empresa; grupo) ou da versão. */
  economy: number | null;
  /** Variação somada do nó, ou da versão contra a anterior. Nulo = sem versão anterior. */
  variation: number | null;
  /** Dia mais antigo do pedido sem variação: é a primeira versão gravada. */
  isFirstVersion: boolean;
  /** Autores dos reprocessos agrupados. */
  authors?: string;
  entry?: DashboardEconomyTimelineEntry;
}

/** O que a árvore precisa saber da tela: régua, nomes e datas formatadas. */
export interface TimelineTreeContext {
  value: (entry: DashboardEconomyTimelineEntry) => number;
  delta: (entry: DashboardEconomyTimelineEntry) => number | null;
  companyName: (companyId: string) => string;
  dayKey: (entry: DashboardEconomyTimelineEntry) => string;
  dayLabel: (entry: DashboardEconomyTimelineEntry) => string;
  /** Sem o nível de empresa (filtro em uma empresa só). */
  skipCompanyLevel: boolean;
  /** Dia mais antigo de todo o histórico carregado (antes dos filtros). */
  oldestDayKey: string;
  /** Estado de expansão: o que a pessoa abriu ou fechou vence o padrão. */
  isExpanded: (key: string, defaultValue: boolean) => boolean;
}

const MANUAL_REPROCESS = 'ManualReprocess';

function sumDeltas(
  entries: DashboardEconomyTimelineEntry[],
  ctx: TimelineTreeContext
): number | null {
  const deltas = entries.map(e => ctx.delta(e)).filter((d): d is number => d !== null);
  return deltas.length ? deltas.reduce((sum, d) => sum + d, 0) : null;
}

function countEvents(entries: DashboardEconomyTimelineEntry[]): number {
  return entries.filter(e => !isAutomaticEconomyTrigger(e.trigger)).length;
}

function groupBy<T>(items: T[], keyOf: (item: T) => string): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const key = keyOf(item);
    const list = groups.get(key) ?? [];
    list.push(item);
    groups.set(key, list);
  }
  return groups;
}

function entryNode(
  entry: DashboardEconomyTimelineEntry,
  ctx: TimelineTreeContext
): TreeNode<TimelineRow> {
  return {
    leaf: true,
    data: {
      kind: 'entry',
      label: '',
      versionCount: 1,
      eventCount: countEvents([entry]),
      economy: ctx.value(entry),
      variation: ctx.delta(entry),
      isFirstVersion: false,
      entry,
    },
  };
}

function isSilentReprocess(
  entry: DashboardEconomyTimelineEntry,
  ctx: TimelineTreeContext
): boolean {
  const delta = ctx.delta(entry);
  return entry.trigger === MANUAL_REPROCESS && delta !== null && isZeroAmount(delta);
}

/**
 * Versões de uma empresa num dia. Reprocessos manuais seguidos sem variação viram um nó
 * "Reprocesso manual ×N, sem variação" (fechado por padrão).
 */
function entryNodes(
  entries: DashboardEconomyTimelineEntry[],
  parentKey: string,
  ctx: TimelineTreeContext
): TreeNode<TimelineRow>[] {
  const nodes: TreeNode<TimelineRow>[] = [];
  let run: DashboardEconomyTimelineEntry[] = [];

  const flush = (): void => {
    if (run.length >= 2) {
      const key = `${parentKey}|r:${run[0].generatedAt}`;
      const authors = [
        ...new Set(run.map(e => e.triggeredByName).filter((n): n is string => !!n)),
      ].join(', ');
      nodes.push({
        key,
        expanded: ctx.isExpanded(key, false),
        children: run.map(e => entryNode(e, ctx)),
        data: {
          kind: 'reprocessGroup',
          label: `Reprocesso manual ×${run.length}`,
          versionCount: run.length,
          eventCount: run.length,
          economy: ctx.value(run[0]),
          variation: 0,
          isFirstVersion: false,
          authors,
        },
      });
    } else {
      nodes.push(...run.map(e => entryNode(e, ctx)));
    }
    run = [];
  };

  for (const entry of entries) {
    if (isSilentReprocess(entry, ctx)) {
      run.push(entry);
      continue;
    }
    flush();
    nodes.push(entryNode(entry, ctx));
  }
  flush();
  return nodes;
}

/**
 * Monta a árvore Dia → Empresa → Versões. As versões chegam da mais nova para a mais antiga, então
 * a primeira de cada grupo é o fim do dia. Só o dia mais recente (e as empresas dele) nasce aberto.
 */
export function buildTimelineTree(
  entries: DashboardEconomyTimelineEntry[],
  ctx: TimelineTreeContext
): TreeNode<TimelineRow>[] {
  const days = groupBy(entries, e => ctx.dayKey(e));
  let isLatestDay = true;
  const tree: TreeNode<TimelineRow>[] = [];

  for (const [dayKey, dayEntries] of days) {
    const key = `d:${dayKey}`;
    const variation = sumDeltas(dayEntries, ctx);

    let children: TreeNode<TimelineRow>[];
    if (ctx.skipCompanyLevel) {
      children = entryNodes(dayEntries, key, ctx);
    } else {
      const companies = groupBy(dayEntries, e => e.companyId);
      children = [...companies.entries()]
        .map(([companyId, companyEntries]) => ({
          companyId,
          companyEntries,
          name: ctx.companyName(companyId) || 'Empresa',
        }))
        .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
        .map(({ companyId, companyEntries, name }) => {
          const companyKey = `${key}|c:${companyId}`;
          return {
            key: companyKey,
            expanded: ctx.isExpanded(companyKey, isLatestDay),
            children: entryNodes(companyEntries, companyKey, ctx),
            data: {
              kind: 'company' as const,
              label: name,
              versionCount: companyEntries.length,
              eventCount: countEvents(companyEntries),
              economy: ctx.value(companyEntries[0]),
              variation: sumDeltas(companyEntries, ctx),
              isFirstVersion: false,
            },
          };
        });
    }

    tree.push({
      key,
      expanded: ctx.isExpanded(key, isLatestDay),
      children,
      data: {
        kind: 'day',
        label: ctx.dayLabel(dayEntries[0]),
        versionCount: dayEntries.length,
        eventCount: countEvents(dayEntries),
        // Com várias empresas, somar o fim do dia só das que tiveram versão engana: fica vazio.
        economy: ctx.skipCompanyLevel ? ctx.value(dayEntries[0]) : null,
        variation,
        isFirstVersion: dayKey === ctx.oldestDayKey && variation === null,
      },
    });
    isLatestDay = false;
  }

  return tree;
}

/** Chaves de todos os nós com filhos, para expandir ou recolher tudo. */
export function collectParentKeys(nodes: TreeNode<TimelineRow>[]): string[] {
  const keys: string[] = [];
  const walk = (list: TreeNode<TimelineRow>[]): void => {
    for (const node of list) {
      if (node.key && node.children?.length) {
        keys.push(node.key);
        walk(node.children);
      }
    }
  };
  walk(nodes);
  return keys;
}
